import {
    Box,
    Button, Checkbox,
    Flex,
    FormControl, FormErrorMessage,
    FormLabel, IconButton,
    Input, Link, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Text,
    Textarea, Tooltip, Menu, MenuButton, MenuList, MenuItem, Portal
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Formik, Form, Field } from 'formik';
import { useNavigate } from 'react-router-dom';
import EventsService from '../../../../services/events/eventsService';
import { useState } from "react";
import FileUploadInput from "../../../../shared/components/FileUploadInput/FileUploadInput";
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import TicketModal from '../../../../shared/components/Ticket/TicketModal';
import { useTranslation } from "react-i18next";
import ErrorInput from "../../../../shared/components/ErrorInput/ErrorInput";
import { createEventValidationSchema } from "../../../../utils/validation/schemas";
import ErrorEventForm from "../../components/ErrorEventForm/ErrorEventForm";
import { FaClipboard } from 'react-icons/fa';
import { normalizeTickets } from '../../../../utils/eventHelpers';
import { phonePrefixes } from '../../../../utils/phonePrefixes';
import ReactCountryFlag from "react-country-flag";

import QuillEditor from "../../../../shared/components/QuillEditor/QuillEditor";
import { useAppToast } from "../../../../hooks/useAppToast";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";
import AddressAutocompleteInput from "../../../../shared/components/AddressAutocompleteInput/AddressAutocompleteInput";
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";

const AddEventForm = () => {
    const { isAdmin, checkIntegrationStatus } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation("default");

    const toast = useAppToast();
    const [error, setError] = useState(null);
    const [coverImage, setCoverImage] = useState(null);
    const [coverImageName, setCoverImageName] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableHashtags, setAvailableHashtags] = useState([]);

    const [tickets, setTickets] = useState([]);
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [editingTicket, setEditingTicket] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);

    const [isPasteOpen, setPasteOpen] = useState(false);
    const [pasteText, setPasteText] = useState('');

    const [formValues, setFormValues] = useState({
        title: '',
        address: '',
        directions: '',
        url: '',
        platform: '',
        startDate: dayjs().format('YYYY-MM-DD'),
        startTime: dayjs().format('HH:mm'),
        endDate: '',
        endTime: '',
        maxParticipants: '',
        showFreeSlotsInfo: false,
        description: '',
        categories: [],
        hashtags: [],
        eventType: 'offline',
        isFree: false,
        ticketType: 'paid',
        hostName: '',
        hostPhonePrefix: '+48',
        hostPhoneNumber: '',
        hostEmail: '',
        hostInfo: ''
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        
        const fetchData = async () => {
            try {
                const categoriesResponse = await EventsService.getCategories();
                const hashtagsResponse = await EventsService.getHashtags();

                setAvailableCategories(categoriesResponse.data.map(c => ({ value: c.name, label: c.name })));
                setAvailableHashtags(hashtagsResponse.data.data.map(h => ({ value: h.name, label: h.name })));
            } catch (err) {
                toast({
                    title: t('request.category.error'),
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchData();
    }, []);

    const handleUploadImage = async (file) => {
        setIsUploading(true);
        try {
            const response = await EventsService.uploadCoverImage(file);
            setCoverImageName(file.name);
            setCoverImage(response.data);
        } catch (error) {
            setError(error);
            toast({
                title: t('upload.error'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = async (values, actions) => {
        try {
            if (!coverImage) {
                toast({
                    title: t('validation.error', 'Błąd walidacji'),
                    description: t('event.error.cover_image_empty', 'Wydarzenie musi posiadać zdjęcie.'),
                    status: 'error', duration: 5000, isClosable: true,
                });
                actions.setSubmitting(false);
                return;
            }

            if (values.ticketType === 'paid' && tickets.length === 0) {
                toast({
                    title: t('validation.error', 'Błąd walidacji'),
                    description: t('event.error.tickets_empty', 'Wydarzenie płatne wymaga dodania przynajmniej jednego biletu.'),
                    status: 'error', duration: 5000, isClosable: true,
                });
                actions.setSubmitting(false);
                return;
            }

            if (values.ticketType === 'paid') {
                const { hasPayment, hasKsef } = await checkIntegrationStatus();
                if (!hasPayment || !hasKsef) {
                    toast({
                        title: t('event.error.configure_payment_title'),
                        description: (
                            <Box>
                                <Text>{t('event.error.integration_incomplete')}</Text>
                                <Link
                                    href="/setup"
                                    isExternal
                                    display="block"
                                    mt={2}
                                    fontWeight="bold"
                                    textDecoration="underline"
                                >
                                    {t('event.error.go_to_account_settings')}
                                </Link>
                            </Box>
                        ),
                        status: 'error',
                        duration: null,
                        isClosable: true,
                    });
                    actions.setSubmitting(false);
                    return;
                }
            }

            // Guard: istniejące bilety vs koniec wydarzenia
            const eventEndISO = values.endDate && values.endTime
                ? dayjs(`${values.endDate}T${values.endTime}`)
                : null;
            if (eventEndISO && eventEndISO.isValid()) {
                const offenders = tickets.filter(t =>
                    (t.isLimitedTime && t.saleEndDate && t.saleEndTime) &&
                    dayjs(`${t.saleEndDate}T${t.saleEndTime}`).isAfter(eventEndISO)
                );
                if (offenders.length) {
                    toast({
                        title: t('event.validation.ticket_sale_after_event_title'),
                        description: `${t('event.validation.ticket_sale_after_event_desc')}: ${offenders.map(o => o.name || '(bez nazwy)').join(', ')}`,
                        status: 'error', duration: 5000, isClosable: true,
                    });
                    actions.setSubmitting(false);
                    return;
                }
            }
            const safeTickets = normalizeTickets(tickets);
            await EventsService.create({
                title: values.title,
                parentTitle: values.parentEventTitle,
                address: values.address,
                url: values.url,
                platform: values.platform,
                startDate: values.startDate,
                startTime: values.startTime,
                endDate: values.endDate,
                endTime: values.endTime,
                maxParticipants: values.ticketType === 'free'
                    ? values.maxParticipants
                    : tickets.reduce((acc, ticket) => acc + (parseInt(ticket.availableQuantity) || 0), 0),
                description: values.description,
                coverImage: coverImage,
                coverImageName: coverImageName,
                isOnline: values.eventType === 'online',
                isFree: values.ticketType === 'free',
                showFreeSlotsInfo: values.showFreeSlotsInfo,
                directions: values.directions,
                categories: values.categories,
                hashtags: values.hashtags,
                host: {
                    name: values.hostName,
                    phone: values.hostPhoneNumber ? `${values.hostPhonePrefix} ${values.hostPhoneNumber}` : '',
                    email: values.hostEmail,
                    info: values.hostInfo
                },
                tickets: safeTickets
            });
            toast({
                title: t('event.added'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            setError(null);
            navigate('/');
        } catch (error) {
            setError(error.response?.data);
            toast({
                title: t('event.error.added'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            actions.setSubmitting(false);
        }
    };

    const applyJson = (parsed) => {
        let parsedPhonePrefix = '+48';
        let parsedPhoneNumber = '';
        if (parsed.host?.phone) {
            const phoneParts = parsed.host.phone.match(/^(\+\d+)\s*(.*)$/);
            if (phoneParts) {
                parsedPhonePrefix = phoneParts[1];
                parsedPhoneNumber = phoneParts[2].replace(/\D/g, '');
            } else {
                parsedPhoneNumber = parsed.host.phone.replace(/\D/g, '');
            }
        }

        const formValues = {
            title: parsed.title || '',
            address: parsed.address || '',
            directions: parsed.directions || '',
            url: parsed.url || '',
            platform: parsed.platform || '',
            startDate: parsed.startDate || '',
            startTime: parsed.startTime || '',
            endDate: parsed.endDate || '',
            endTime: parsed.endTime || '',
            maxParticipants: parsed.maxParticipants || '',
            showFreeSlotsInfo: parsed.showFreeSlotsInfo || false,
            description: parsed.description || '',
            categories: parsed.categories || [],
            hashtags: parsed.hashtags || [],
            eventType: parsed.isOnline ? 'online' : 'offline',
            isFree: !!parsed.isFree,
            ticketType: parsed.isFree ? 'free' : 'paid',
            hostName: parsed.host?.name || '',
            hostPhonePrefix: parsedPhonePrefix,
            hostPhoneNumber: parsedPhoneNumber,
            hostEmail: parsed.host?.email || '',
            hostInfo: parsed.host?.info || '',
        };

        setFormValues(formValues);
        const safeTickets = normalizeTickets(parsed.ticketTypes);
        setTickets(safeTickets || []);
        setCoverImage(parsed.coverImage || null);
        setCoverImageName(parsed.originalCoverImageName || null);
    };

    const handleJsonPaste = async () => {
        try {
            if (!document.hasFocus()) {
                // przywrócić fokus; bywa ignorowane, ale warto
                window.focus?.();
                if (!document.hasFocus()) throw new Error('not-focused');
            }

            const text = await navigator.clipboard.readText(); // z gestu użytkownika (onClick)
            const parsed = JSON.parse(text);
            applyJson(parsed);

            toast({ title: t('event.json_import.success'), status: 'success', duration: 3000, isClosable: true, colorScheme: "orange" });
        } catch (err) {
            // fallback – ręczne wklejenie
            setPasteOpen(true);
            toast({
                title: t('event.json_import.error_title'),
                description: t('event.json_import.error_desc'),
                status: 'warning',
                duration: 4000,
                isClosable: true,
                colorScheme: "orange"
            });
        }
    };
    const formatCreateLabel = (inputValue) => `${t('form.create')} "${inputValue}"`;

    return (
        <>
            <Flex w="100%" maxW={{ base: "full", sm: "7xl" }} flexDirection="column" px={{ base: 4, sm: 4 }} py={{ base: "40px", sm: '80px' }} gap={'16px'}>
                <Flex w="100%" maxW={{ base: "full", sm: "7xl" }} flexDirection={{ base: "column", md: "row" }} className="nav-wrapper" gap="16px" cursor={"pointer"} align={{ base: "flex-start", md: "center" }} justifyContent="space-between">
                    <Flex gap="16px" align="center" onClick={() => navigate(`/`)}>
                        <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 15.5L1 8.5M1 8.5L8 1.5M1 8.5H19" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <Text className="nav">{t('event.add.title')}</Text>
                    </Flex>
                    {isAdmin && <Tooltip label={t('event.json_import.tooltip')}>
                        <IconButton
                            icon={<FaClipboard />}
                            aria-label={t('event.json_import.tooltip')}
                            onClick={handleJsonPaste}
                            size="sm"
                            variant="ghost"
                        />
                    </Tooltip>}
                </Flex>
                {error && <ErrorEventForm error={error} t={t} name="event.add.error" />}
                <Flex w="100%" maxW={{ base: "full", sm: "7xl" }} className="event">
                    <Flex flexDirection={"column"} gap={'2px'} p={{ base: "16px", md: "16px" }} pb={0}>
                        <Text className="title">
                            {t('event.tab.about')}
                        </Text>
                        <Text className="subtitle">
                            {t('event.add.subtitle')}
                        </Text>
                    </Flex>
                    <Formik
                        initialValues={formValues}
                        validateOnBlur={true}
                        validateOnChange={true}
                        validationSchema={createEventValidationSchema(t)}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({ isSubmitting, errors, touched, values, isValid, setFieldValue, handleChange, handleBlur }) => (
                            <Form className="form">
                                <Flex direction={{ base: 'column', md: 'row' }} gap={6} p={{ base: "16px", md: "16px" }} pt={{ base: "8px", md: "8px" }}>
                                    <Flex direction={"column"} flex={1} gap={4}>
                                        <InlineErrorInput name="title" label={t('event.form.name')} value={values.title}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.title} touched={touched.title} isRequired
                                        />

                                        <FormControl mb={4} isInvalid={errors.categories && touched.categories}>
                                            <FormLabel>{t('event.form.category')}</FormLabel>
                                            <Field name="categories">
                                                {({ field, form }) => (
                                                    <>
                                                        <Select
                                                            isMulti
                                                            options={availableCategories}
                                                            value={field.value.map(val => ({ value: val, label: val }))}
                                                            onChange={selectedOptions => {
                                                                const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                                                form.setFieldValue('categories', values);
                                                            }}
                                                            placeholder={t('event.form.placeholder.category')}
                                                            isClearable
                                                            isSearchable
                                                            maxMenuHeight={150}
                                                            className="categories-select"
                                                            onBlur={() => form.setFieldTouched('categories', true)}
                                                        />
                                                        <FormErrorMessage>{errors.categories}</FormErrorMessage>
                                                    </>
                                                )}
                                            </Field>
                                        </FormControl>
                                        <FormControl mb={4} isInvalid={errors.hashtags && touched.hashtags}>
                                            <FormLabel>{t('event.form.hashtags')}</FormLabel>
                                            <Field name="hashtags">
                                                {({ field, form }) => (
                                                    <>
                                                        <CreatableSelect
                                                            isMulti
                                                            options={availableHashtags}
                                                            value={field.value.map(val => ({ value: val, label: val }))}
                                                            onChange={selectedOptions => {
                                                                const values = selectedOptions ? selectedOptions.map(opt => opt.value.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean) : [];
                                                                const uniqueValues = [...new Set(values)];
                                                                form.setFieldValue('hashtags', uniqueValues);
                                                            }}
                                                            placeholder={t('event.form.placeholder.hashtags')}
                                                            isClearable
                                                            isSearchable
                                                            maxMenuHeight={150}
                                                            className="hashtags-select"
                                                            formatCreateLabel={formatCreateLabel}
                                                        />
                                                        <FormErrorMessage>{errors.hashtags}</FormErrorMessage>
                                                    </>
                                                )}
                                            </Field>
                                        </FormControl>

                                        <FileUploadInput coverImage={coverImage} coverImageName={coverImageName} onFileSelect={handleUploadImage} />
                                        <Flex gap={4} alignItems="flex-start" sx={{ '& label': { minH: { base: '44px', md: 'auto' }, display: 'flex', alignItems: 'flex-end', mb: 1, pb: 0 }, '& > div': { flex: 1 } }}>
                                            <ErrorInput name="startDate" type="date" value={values.startDate} label={t('event.form.startDate')} isRequired />
                                            <ErrorInput name="startTime" type="time" value={values.startTime} label={t('event.form.startTime')} isRequired />
                                        </Flex>
                                        <Flex gap={4} alignItems="flex-start" sx={{ '& label': { minH: { base: '44px', md: 'auto' }, display: 'flex', alignItems: 'flex-end', mb: 1, pb: 0 }, '& > div': { flex: 1 } }}>
                                            <ErrorInput name="endDate" type="date" value={values.endDate} label={t('event.form.endDate')} isRequired />
                                            <ErrorInput name="endTime" type="time" value={values.endTime} label={t('event.form.endTime')} isRequired />
                                        </Flex>
                                        <FormControl mb={4} isInvalid={errors.eventType && touched.eventType}>
                                            <Field name="eventType">
                                                {({ field, form }) => (
                                                    <>
                                                        <Flex pb="1px" pt={'8px'}>
                                                            <Box
                                                                px={4}
                                                                py={'16px'}
                                                                flex="1"
                                                                textAlign={"center"}
                                                                borderBottom={field.value === 'offline' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                                cursor="pointer"
                                                                onClick={() => form.setFieldValue('eventType', 'offline')}
                                                            >

                                                                <Text fontWeight="medium">
                                                                    <Checkbox
                                                                        isChecked={field.value === 'offline'}
                                                                        pointerEvents="none"
                                                                        mr={'10px'}
                                                                        mt={'5px'}
                                                                    />
                                                                    {t('event.form.eventType.offline')}
                                                                </Text>
                                                            </Box>
                                                            <Box
                                                                px={4}
                                                                py={'16px'}
                                                                flex="1"
                                                                textAlign={"center"}
                                                                borderBottom={field.value === 'online' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                                cursor="pointer"
                                                                onClick={() => form.setFieldValue('eventType', 'online')}
                                                            >
                                                                <Text fontWeight="medium">
                                                                    <Checkbox
                                                                        isChecked={field.value === 'online'}
                                                                        pointerEvents="none"
                                                                        mr={'10px'}
                                                                        mt={'5px'}
                                                                    />
                                                                    {t('event.form.eventType.online')}
                                                                </Text>
                                                            </Box>
                                                        </Flex>
                                                        <FormErrorMessage>{errors.eventType}</FormErrorMessage>
                                                    </>
                                                )}
                                            </Field>
                                        </FormControl>
                                        <Field name="eventType">
                                            {({ field }) =>
                                                field.value === 'offline' ? (
                                                    <>
                                                        <AddressAutocompleteInput name="address" label={t('event.form.eventType.address')} value={values.address}
                                                            onChange={handleChange} onBlur={handleBlur} error={errors.address} touched={touched.address} isRequired={values.eventType === 'offline'}
                                                        />
                                                        <InlineErrorInput name="directions" label={t('event.form.eventType.directions')} value={values.directions}
                                                            onChange={handleChange} onBlur={handleBlur} error={errors.directions} touched={touched.directions}
                                                            as={Textarea} rows={4} h={'100%'}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <InlineErrorInput name="platform" label={t('event.form.eventType.platform', 'Nazwa platformy')} value={values.platform}
                                                            onChange={handleChange} onBlur={handleBlur} error={errors.platform} touched={touched.platform} isRequired={values.eventType === 'online'}
                                                        />
                                                        <InlineErrorInput name="url" label={t('event.form.eventType.url', 'Link do wydarzenia')} value={values.url}
                                                            onChange={handleChange} onBlur={handleBlur} error={errors.url} touched={touched.url} isRequired={values.eventType === 'online'}
                                                        />
                                                    </>
                                                )
                                            }
                                        </Field>
                                    </Flex>
                                    <Box flex={1}>
                                        <QuillEditor
                                            label={t('event.form.description', 'Opis wydarzenia')}
                                            value={values.description}
                                            onChange={(val) => setFieldValue('description', val)}
                                        />
                                        {errors.description && touched.description && (
                                            <FormErrorMessage style={{ display: 'block' }}>{errors.description}</FormErrorMessage>
                                        )}
                                    </Box>
                                </Flex>
                                <Flex w="100%" bg="#F3F3F3" h={'32px'}>
                                </Flex>
                                <Flex justify="center" gap={4} flexDirection={"column"} p={{ base: "16px", md: "16px" }}>

                                    <Box w="100%" bg="white" borderRadius="4px">
                                        <Text className="title">{t('event.form.title')}</Text>
                                        <Text className="subtitle">
                                            {t('event.form.subtitle')}
                                        </Text>
                                        <FormControl mb={4} isInvalid={errors.ticketType && touched.ticketType}>
                                            <Field name="ticketType">
                                                {({ field, form }) => (
                                                    <>
                                                        <Flex pb="1px" pt={'8px'}>
                                                            <Box
                                                                px={4}
                                                                py={'16px'}
                                                                flex="1"
                                                                textAlign={"center"}
                                                                borderBottom={field.value === 'paid' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                                cursor="pointer"
                                                                onClick={() => form.setFieldValue('ticketType', 'paid')}
                                                            >
                                                                <Flex align="center" justify="center" fontWeight="medium">
                                                                    <Checkbox
                                                                        isChecked={field.value === 'paid'}
                                                                        pointerEvents="none"
                                                                        mr={'10px'}
                                                                        mt={'5px'}
                                                                    />
                                                                    {t('event.form.ticketType.paid')}
                                                                </Flex>
                                                            </Box>
                                                            <Box
                                                                px={4}
                                                                py={'16px'}
                                                                flex="1"
                                                                textAlign={"center"}
                                                                borderBottom={field.value === 'free' ? '3px solid #089A7A' : '3px solid #EEEEEE'}
                                                                cursor="pointer"
                                                                onClick={() => form.setFieldValue('ticketType', 'free')}
                                                            >
                                                                <Flex align="center" justify="center" fontWeight="medium">
                                                                    <Checkbox
                                                                        isChecked={field.value === 'free'}
                                                                        pointerEvents="none"
                                                                        mr={'10px'}
                                                                        mt={'5px'}
                                                                    />
                                                                    {t('event.form.ticketType.free')}
                                                                </Flex>
                                                            </Box>
                                                        </Flex>
                                                        <FormErrorMessage>{errors.ticketType}</FormErrorMessage>
                                                        {/* Dla ticketType === 'paid' > tabelka */}
                                                        {field.value === 'paid' && (
                                                            <Box mb={4} mt={4}>
                                                                {tickets.length > 0 ? (
                                                                    <Box w="100%" borderRadius="4px" overflow="hidden">
                                                                        <Box display={{ base: 'none', lg: 'flex' }} px={4} py={2}>
                                                                            <Box flex="2">{t('event.form.tickets.th.kind')}</Box>
                                                                            <Box flex="1">{t('event.form.tickets.th.price')}</Box>
                                                                            <Box flex="2">{t('event.form.tickets.th.from')}</Box>
                                                                            <Box flex="2">{t('event.form.tickets.th.to')}</Box>
                                                                            <Box flex="1" textAlign="center">{t('event.form.tickets.th.quantity')}</Box>
                                                                            <Box flex="0.5"></Box>
                                                                        </Box>
                                                                        <Flex direction="column" gap={{ base: 4, lg: 0 }}>
                                                                        {tickets.map((ticket, index) => (
                                                                            <Box key={index} display="flex" flexDirection={{ base: 'column', lg: 'row' }} bg="#F3F3F3" fontWeight="bold" alignItems={{ base: 'flex-start', lg: 'center' }} px={4} py={{ base: 4, lg: 2 }} borderRadius={{ base: 4, lg: 0 }}>
                                                                                <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.kind')}:</Text>
                                                                                    <Text as="span">{ticket.name}</Text>
                                                                                </Box>
                                                                                <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.price')}:</Text>
                                                                                    <Text as="span">{ticket.price} zł</Text>
                                                                                </Box>
                                                                                <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.from')}:</Text>
                                                                                    <Text as="span" textAlign={{ base: "right", lg: "left" }}>{ticket.saleStartDate ?? '--:--'} {ticket.saleStartTime ?? '--:--'}</Text>
                                                                                </Box>
                                                                                <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.to')}:</Text>
                                                                                    <Text as="span" textAlign={{ base: "right", lg: "left" }}>{ticket.saleEndDate ?? '--:--'} {ticket.saleEndTime ?? '--:--'}</Text>
                                                                                </Box>
                                                                                <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'center' }} w="100%" mb={{ base: 4, lg: 0 }}>
                                                                                    <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('event.form.tickets.th.quantity')}:</Text>
                                                                                    <Text as="span" textAlign={{ base: "right", lg: "center" }}>{ticket.availableQuantity}</Text>
                                                                                </Box>
                                                                                <Box flex="0.5" display="flex" gap={2} justifyContent={{ base: 'flex-start', lg: 'flex-end' }} w="100%">
                                                                                    <Button size="sm" onClick={() => {
                                                                                        setTickets(prev => prev.filter((_, i) => i !== index));
                                                                                    }}>
                                                                                        <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                            <path d="M14.125 4.55556L13.3664 15.3484C13.3349 15.797 13.1374 16.2167 12.8135 16.5232C12.4896 16.8296 12.0634 17 11.6208 17H4.37925C3.93662 17 3.51043 16.8296 3.18652 16.5232C2.86262 16.2167 2.66505 15.797 2.63363 15.3484L1.875 4.55556M6.25 8.11111V13.4444M9.75 8.11111V13.4444M10.625 4.55556V1.88889C10.625 1.65314 10.5328 1.42705 10.3687 1.26035C10.2046 1.09365 9.98206 1 9.75 1H6.25C6.01794 1 5.79538 1.09365 5.63128 1.26035C5.46719 1.42705 5.375 1.65314 5.375 1.88889V4.55556M1 4.55556H15" stroke="#089A7A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        </svg>
                                                                                    </Button>
                                                                                    <Button size="sm" onClick={() => {
                                                                                        setEditingTicket({ ...ticket });
                                                                                        setEditingIndex(index);
                                                                                        setIsTicketModalOpen(true);
                                                                                    }}>
                                                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                            <path d="M17.414 2.58594C17.0389 2.211 16.5303 2.00037 16 2.00037C15.4697 2.00037 14.9611 2.211 14.586 2.58594L7 10.1719V12.9999H9.828L17.414 5.41394C17.7889 5.03888 17.9996 4.53027 17.9996 3.99994C17.9996 3.46961 17.7889 2.96099 17.414 2.58594Z" fill="#089A7A" />
                                                                                            <path fillRule="evenodd" clipRule="evenodd" d="M2 6C2 5.46957 2.21071 4.96086 2.58579 4.58579C2.96086 4.21071 3.46957 4 4 4H8C8.26522 4 8.51957 4.10536 8.70711 4.29289C8.89464 4.48043 9 4.73478 9 5C9 5.26522 8.89464 5.51957 8.70711 5.70711C8.51957 5.89464 8.26522 6 8 6H4V16H14V12C14 11.7348 14.1054 11.4804 14.2929 11.2929C14.4804 11.1054 14.7348 11 15 11C15.2652 11 15.5196 11.1054 15.7071 11.2929C15.8946 11.4804 16 11.7348 16 12V16C16 16.5304 15.7893 17.0391 15.4142 17.4142C15.0391 17.7893 14.5304 18 14 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V6Z" fill="#089A7A" />
                                                                                        </svg>
                                                                                        ️</Button>
                                                                                </Box>
                                                                            </Box>
                                                                        ))}
                                                                        </Flex>
                                                                    </Box>
                                                                ) : (
                                                                    <Text mt={2} mb={2}>{t('event.form.tickets.not_found')}</Text>
                                                                )}

                                                                <Flex justify="center" gap={4} mt={6}>
                                                                    <Button
                                                                        onClick={() => {
                                                                            setEditingTicket(null);
                                                                            setEditingIndex(null);
                                                                            setIsTicketModalOpen(true);
                                                                        }}
                                                                        isLoading={isSubmitting} bg="brand.500" color="white" borderRadius={'4px'} _hover={{ bg: 'brand.600' }} px="48px" py="25px">
                                                                        <Text mr={'10px'}>
                                                                            {t('event.form.tickets.add')}
                                                                        </Text>
                                                                        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M8.5 5.66667V8M8.5 8V10.3333M8.5 8H10.8333M8.5 8H6.16667M15.5 8C15.5 8.91925 15.3189 9.82951 14.9672 10.6788C14.6154 11.5281 14.0998 12.2997 13.4497 12.9497C12.7997 13.5998 12.0281 14.1154 11.1788 14.4672C10.3295 14.8189 9.41925 15 8.5 15C7.58075 15 6.6705 14.8189 5.82122 14.4672C4.97194 14.1154 4.20026 13.5998 3.55025 12.9497C2.90024 12.2997 2.38463 11.5281 2.03284 10.6788C1.68106 9.82951 1.5 8.91925 1.5 8C1.5 6.14348 2.2375 4.36301 3.55025 3.05025C4.86301 1.7375 6.64348 1 8.5 1C10.3565 1 12.137 1.7375 13.4497 3.05025C14.7625 4.36301 15.5 6.14348 15.5 8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </Button>
                                                                </Flex>
                                                            </Box>
                                                        )}
                                                        <TicketModal
                                                            isOpen={isTicketModalOpen}
                                                            onClose={() => { setIsTicketModalOpen(false); setEditingTicket(null); setEditingIndex(null); }}
                                                            onSave={(ticketData) => {
                                                                if (editingIndex !== null) {
                                                                    setTickets(prev =>
                                                                        prev.map((t, i) => (i === editingIndex ? ticketData : t))
                                                                    );
                                                                } else {
                                                                    setTickets(prev => [...prev, ticketData]);
                                                                }
                                                            }}
                                                            initialData={editingTicket}
                                                            eventEndISO={
                                                                values.endDate && values.endTime
                                                                    ? `${values.endDate}T${values.endTime}`
                                                                    : null
                                                            }
                                                        />

                                                        {/* Dla ticketType === 'free' > dwa pola */}
                                                        {field.value === 'free' && (
                                                            <>
                                                                <InlineErrorInput name="maxParticipants" type="number" label={t('event.form.maxParticipants')} value={values.maxParticipants} mb={2}
                                                                    onChange={handleChange} onBlur={handleBlur} error={errors.maxParticipants} touched={touched.maxParticipants} isRequired
                                                                />
                                                                <FormControl mb={4}>
                                                                    <Checkbox
                                                                        isChecked={form.values.showFreeSlotsInfo}
                                                                        onChange={e => form.setFieldValue('showFreeSlotsInfo', e.target.checked)}
                                                                    >
                                                                        {t('event.form.showFreeSlotsInfo')}
                                                                    </Checkbox>
                                                                </FormControl>
                                                            </>
                                                        )}

                                                    </>
                                                )}
                                            </Field>
                                        </FormControl>
                                    </Box>
                                </Flex>

                                <Flex w="100%" bg="#F3F3F3" h={'32px'}>
                                </Flex>

                                <Flex justify="center" gap={4} flexDirection={"column"} p={{ base: "16px", md: "16px" }}>

                                    <Flex direction="column" gap={4} w="100%" bg="white" borderRadius="4px">
                                        <Text fontSize="lg" fontWeight="bold" mb={2}>{t('event.form.host.title')}</Text>
                                        <InlineErrorInput name="hostName" label={t('event.form.host.name')} value={values.hostName}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.hostName} touched={touched.hostName} isRequired
                                        />
                                        <InlineErrorInput name="hostPhoneNumber" label={t('event.form.host.phone')} value={values.hostPhoneNumber} placeholder="123456789" useFormik={false}
                                            onChange={(e) => {
                                                const numericValue = e.target.value.replace(/\D/g, '');
                                                e.target.value = numericValue;
                                                setFieldValue('hostPhoneNumber', numericValue);
                                            }}
                                            onBlur={handleBlur} error={errors.hostPhoneNumber} touched={touched.hostPhoneNumber}
                                            leftAddon={
                                                <Menu autoSelect={false} strategy="fixed" isLazy>
                                                    <MenuButton
                                                        type="button"
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            outline: 'none',
                                                            cursor: 'pointer',
                                                            padding: '0 8px',
                                                            minWidth: '70px',
                                                            textAlign: 'left'
                                                        }}
                                                    >
                                                        <Flex align="center" gap={2}>
                                                            <ReactCountryFlag
                                                                countryCode={phonePrefixes.find(p => p.value === values.hostPhonePrefix)?.code || 'PL'}
                                                                svg
                                                                style={{ width: '1.2em', height: '1.2em' }}
                                                            />
                                                            <Text>{values.hostPhonePrefix}</Text>
                                                            <ChevronDownIcon w={4} h={4} color="gray.500" />
                                                        </Flex>
                                                    </MenuButton>
                                                    <Portal>
                                                        <MenuList maxH="300px" overflowY="auto" zIndex={1000}>
                                                            {phonePrefixes.map(prefix => (
                                                                <MenuItem
                                                                    key={prefix.code}
                                                                    onClick={() => setFieldValue('hostPhonePrefix', prefix.value)}
                                                                >
                                                                    <Flex align="center" gap={2}>
                                                                        <ReactCountryFlag countryCode={prefix.code} svg style={{ width: '1.2em', height: '1.2em' }} />
                                                                        <Text>{prefix.label}</Text>
                                                                    </Flex>
                                                                </MenuItem>
                                                            ))}
                                                        </MenuList>
                                                    </Portal>
                                                </Menu>
                                            }
                                        />
                                        <InlineErrorInput name="hostEmail" label={t('event.form.host.email')} value={values.hostEmail}
                                            onChange={handleChange} onBlur={handleBlur} error={errors.hostEmail} touched={touched.hostEmail}
                                        />
                                        <Box>
                                            <QuillEditor
                                                label={t('event.form.host.info')}
                                                value={values.hostInfo}
                                                onChange={(val) => setFieldValue('hostInfo', val)}
                                            />
                                            <Text fontSize="sm" color="gray.500" textAlign="right" mt={1}>
                                                {values.hostInfo?.length || 0}/2000
                                            </Text>
                                            {errors.hostInfo && touched.hostInfo && (
                                                <FormErrorMessage style={{ display: 'block' }}>{errors.hostInfo}</FormErrorMessage>
                                            )}
                                        </Box>
                                    </Flex>

                                    <Flex justify="center" gap={4} mt={6}>
                                        <Input type={"hidden"} value={JSON.stringify({ isValid, errors }, null, 2)} />
                                        <Button variant="outline" borderRadius={'4px'} onClick={() => navigate('/')} py="25px">{t('cancel')}</Button>
                                        <Button type="submit" isLoading={isSubmitting || isUploading} isDisabled={!isValid || isUploading} bg="brand.500" color="white" borderRadius={'4px'} _hover={{ bg: 'brand.600' }} px="48px" py="25px">
                                            {t('event.add.title')}
                                        </Button>
                                    </Flex>
                                </Flex>
                            </Form>
                        )}
                    </Formik>
                </Flex>
            </Flex>
            <Modal isOpen={isPasteOpen} onClose={() => setPasteOpen(false)} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('event.json_import.modal_title')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Textarea
                            rows={14}
                            placeholder={t('event.json_import.modal_placeholder')}
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            colorScheme="green"
                            onClick={() => {
                                try {
                                    const parsed = JSON.parse(pasteText);
                                    applyJson(parsed);
                                    setPasteOpen(false);
                                    toast({
                                        title: t('event.json_import.success'),
                                        status: 'success', duration: 3000, isClosable: true
                                    });
                                } catch (e) {
                                    toast({ title: 'Niepoprawny JSON', description: String(e.message), status: 'error', duration: 4000, isClosable: true });
                                }
                            }}
                        >
                            {t('action.load')}
                        </Button>
                        <Button ml={3} onClick={() => setPasteOpen(false)}>{t('action.cancel')}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default AddEventForm;
